import React, { useCallback, useMemo, useRef } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import { Stack, Typography } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Button, LoadingOverlay } from '@travelity/ui';
import Dialog from '@mui/material/Dialog';
import { ProductVariantRequest, useGetProductById } from '@travelity/api';
import { useForm } from '@travelity/form/use-form';
import { useWatch } from 'react-hook-form';
import { MultiSelect, Select } from '@travelity/form/controls';
import SelectProduct from '../select-product/select-product';
import schema from './product-variant-scheme';

export interface ProductVariantModalProps {
    open: boolean;
    isLoading?: boolean;
    setOpen: (v: boolean) => void;
    onConfirm: (v: ProductVariantRequest) => void;
    onClose: () => void;
}

export const ProductVariantModal: React.FC<
    ProductVariantModalProps
> = props => {
    const { open, isLoading, setOpen, onClose, onConfirm } = props;

    const { Form, getValues, errorFields, control } = useForm({
        schema,
        validateInitially: true,
        mode: 'onChange',
        defaultValues: {
            product: undefined,
        } as {
            product?: { value: string };
            capacity?: string;
            options?: string[];
        },
    });

    const selectedProduct = useWatch({ control, name: 'product' });
    const { data: product } = useGetProductById(selectedProduct?.value);

    const handleCancel = useCallback(() => {
        setOpen(false);
        onClose();
    }, [setOpen, onClose]);

    const onSubmit = useCallback(() => {
        const values = getValues();

        onConfirm({
            productId: values.product?.value,
            capacityOptionId: values.capacity,
            productOptionIds: values.options,
        } as ProductVariantRequest);
    }, [getValues]);

    const capacities = useMemo(() => {
        return (
            product?.capacity.capacities.map(capacity => ({
                value: capacity.uuid,
                label: `${capacity.name}`,
            })) || []
        );
    }, [product]);

    const options = useMemo(() => {
        return (
            product?.options.items.map(option => ({
                value: option.id,
                label: `${option.name}`,
            })) || []
        );
    }, [product]);

    return (
        <Form>
            <Dialog
                open={open}
                onClose={handleCancel}
                sx={{
                    '& .MuiDialog-container': {
                        justifyContent: { xs: 'center', sm: 'center' },
                    },
                }}
                PaperProps={{
                    sx: {
                        py: 1,
                        bgcolor: '#fff',
                        maxWidth: '432px',
                        minWidth: { xs: 'calc(100% - 32px)', sm: '450px' },
                        m: { xs: 1, sm: 4 },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontSize: '16px',
                        lineHeight: '16px',
                        fontWeight: 600,
                        color: '#2B395B',
                        pr: 6,
                    }}
                    id="alert-dialog-title"
                >
                    <Typography sx={{ fontWeight: 600 }}>
                        Add Product Variant
                    </Typography>
                </DialogTitle>
                <DialogContent
                    sx={{
                        overflow: 'hidden',
                        pr: 1,
                    }}
                >
                    <Stack
                        sx={{
                            pr: 2,
                            py: 1,
                            height: 'calc(100% - 76px)',
                        }}
                        gap={2}
                    >
                        <SelectProduct
                            name="product"
                            label="Product"
                            placeholder="Search Product"
                        />
                        <Select
                            options={capacities}
                            disabled={!product}
                            width="100%"
                            size="medium"
                            label="Product Capacity"
                            placeholder="Select Capacity"
                            name="capacity"
                        />
                        <MultiSelect
                            label="Product Options"
                            placeholder="Select Options"
                            disabled={!product}
                            options={options}
                            width="100%"
                            name="options"
                        />
                    </Stack>
                    {isLoading && (
                        <LoadingOverlay
                            title="Please wait..."
                            subTitle="It’ll just take a moment."
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        color="neutral"
                        variant="contained"
                        onClick={handleCancel}
                        disabled={isLoading}
                        sx={{
                            px: '26px',
                            py: '10px',
                        }}
                    >
                        Discard
                    </Button>

                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={!!errorFields.length || isLoading}
                        onClick={onSubmit}
                        sx={{
                            py: '10px',
                            px: 4,
                        }}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Form>
    );
};

export const useProductVariantModal = () => {
    const [modalOpen, setModalOpen] = React.useState(false);

    const ref = useRef({
        open: modalOpen,
        setOpen: setModalOpen,
    });
    ref.current = {
        open: modalOpen,
        setOpen: setModalOpen,
    };

    const createProductVariant = useCallback(() => {
        setModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const ProductVariantModalWrapper = useCallback<
        React.FC<Omit<ProductVariantModalProps, 'open' | 'setOpen'>>
    >(props => {
        return ref.current.open ? (
            <ProductVariantModal {...props} {...ref.current} />
        ) : null;
    }, []);

    return {
        ProductVariantModal: ProductVariantModalWrapper,
        modalOpen,
        createProductVariant,
        closeModal,
    };
};
